import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { format, parseISO, subDays } from 'date-fns'
import { useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { DateFilter } from '@/components/ui/date-filter'
import { apiClient } from '@/lib/api/client'
import type {
  SupportChatAnalytics,
  SupportChatAnalyticsParams,
} from '@/lib/api/support-chat-types'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'


export const Route = createFileRoute('/_authenticated/admin/support-chat-analytics')({
  beforeLoad: ({ context }) => {
    // EventPay-internal only. Organizers should see a redirect, not a 403.
    if (!context.auth.user?.is_superuser) {
      throw redirect({ to: '/events' })
    }
  },
  component: AnalyticsPage,
})


const CATEGORY_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#a3a3a3']
const TIER_COLORS: Record<string, string> = {
  t1: '#3b82f6',
  t2: '#10b981',
  t3: '#f59e0b',
}


function AnalyticsPage() {
  const { token } = useAuth()

  const [startDate, setStartDate] = useState(() => format(subDays(new Date(), 30), 'yyyy-MM-dd'))
  const [endDate, setEndDate] = useState(() => format(new Date(), 'yyyy-MM-dd'))

  const params = useMemo<SupportChatAnalyticsParams>(
    () => ({ start_date: startDate, end_date: endDate }),
    [startDate, endDate],
  )

  const { data, isLoading, error } = useQuery({
    queryKey: ['support-chat-analytics', params],
    queryFn: () => apiClient.getSupportChatAnalytics(token ?? '', params),
    enabled: !!token,
  })

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8">
      <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            Support Chat Analytics
          </h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            EventPay-internal view of every organization&apos;s support sessions.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <DateFilter
            granularity="date"
            value={{ gte: startDate || undefined, lte: endDate || undefined }}
            onChange={(v) => { setStartDate(v.gte ?? ''); setEndDate(v.lte ?? '') }}
          />
        </div>
      </header>

      {isLoading && <LoadingState />}
      {error && <ErrorState message={error instanceof Error ? error.message : 'Failed to load'} />}
      {data && <AnalyticsBody data={data} />}
    </div>
  )
}


function AnalyticsBody({ data }: { data: SupportChatAnalytics }) {
  const { totals, repeat_target_rate, sessions_per_day, top_categories, resolution_by_category, tool_usage } = data

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="Total sessions"
          value={totals.total_sessions.toLocaleString()}
        />
        <StatCard
          label="Resolution rate"
          value={`${(totals.resolution_rate * 100).toFixed(0)}%`}
          accent="emerald"
        />
        <StatCard
          label="Avg cost / session"
          value={`$${(totals.avg_cost_cents / 100).toFixed(2)}`}
        />
        <StatCard
          label="Repeat-target rate"
          value={`${(repeat_target_rate.rate * 100).toFixed(0)}%`}
          accent={repeat_target_rate.rate > 0.15 ? 'amber' : 'neutral'}
          tooltip="Sessions where the same attendee comes back within 24h with the same category — likely-miss signal."
        />
      </div>

      <ChartCard title="Sessions per day">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={sessions_per_day}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-neutral-200 dark:stroke-neutral-800" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              tickFormatter={(d) => format(parseISO(d), 'MMM d')}
              className="text-neutral-500 dark:text-neutral-400"
            />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 6 }}
              labelFormatter={(d) => format(parseISO(String(d)), 'MMM d, yyyy')}
            />
            <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={false} name="Sessions" />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard title="Top categories">
          {top_categories.length === 0 ? (
            <EmptyChart message="No classified sessions in this range." />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, top_categories.length * 36)}>
              <BarChart data={top_categories} layout="vertical" margin={{ left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-neutral-200 dark:stroke-neutral-800" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="category"
                  tick={{ fontSize: 11 }}
                  width={140}
                  tickFormatter={categoryLabel}
                />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {top_categories.map((_, idx) => (
                    <Cell key={idx} fill={CATEGORY_COLORS[idx % CATEGORY_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Resolution by category">
          {resolution_by_category.length === 0 ? (
            <EmptyChart message="No resolution data yet." />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, resolution_by_category.length * 40)}>
              <BarChart data={resolution_by_category} layout="vertical" stackOffset="expand">
                <CartesianGrid strokeDasharray="3 3" className="stroke-neutral-200 dark:stroke-neutral-800" />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `${Math.round(Number(v) * 100)}%`}
                />
                <YAxis
                  type="category"
                  dataKey="category"
                  tick={{ fontSize: 11 }}
                  width={140}
                  tickFormatter={categoryLabel}
                />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="resolved" stackId="a" fill="#10b981" name="Resolved" />
                <Bar dataKey="unresolved" stackId="a" fill="#f59e0b" name="Unresolved" />
                <Bar dataKey="escalated" stackId="a" fill="#3b82f6" name="Escalated" />
                <Bar dataKey="unrecorded" stackId="a" fill="#a3a3a3" name="Unrecorded" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <ChartCard title="Tool usage">
        {tool_usage.length === 0 ? (
          <EmptyChart message="No tools have been invoked in this range." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-neutral-200 text-left text-xs uppercase tracking-wide text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
                <tr>
                  <th className="px-3 py-2 font-medium">Tool</th>
                  <th className="px-3 py-2 font-medium">Tier</th>
                  <th className="px-3 py-2 text-right font-medium">Invocations</th>
                  <th className="px-3 py-2 text-right font-medium">Success rate</th>
                  <th className="px-3 py-2 text-right font-medium">Avg duration</th>
                </tr>
              </thead>
              <tbody>
                {tool_usage.map((row) => (
                  <tr key={row.tool_name} className="border-b border-neutral-100 dark:border-neutral-900">
                    <td className="px-3 py-2 font-mono text-xs">{row.tool_name}</td>
                    <td className="px-3 py-2">
                      <span
                        className="inline-flex h-5 items-center rounded-full px-2 text-[10px] font-medium uppercase text-white"
                        style={{ backgroundColor: TIER_COLORS[row.tier] || '#737373' }}
                      >
                        {row.tier}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.invocations.toLocaleString()}</td>
                    <td
                      className={cn(
                        'px-3 py-2 text-right tabular-nums',
                        row.success_rate >= 95
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : row.success_rate >= 80
                          ? 'text-neutral-700 dark:text-neutral-200'
                          : 'text-amber-600 dark:text-amber-400',
                      )}
                    >
                      {row.success_rate.toFixed(0)}%
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-neutral-500 dark:text-neutral-400">
                      {row.avg_duration_ms}ms
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ChartCard>

      <ChartCard title="Cost & duration">
        <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
          <Detail label="Total spend" value={`$${(totals.total_cost_cents / 100).toFixed(2)}`} />
          <Detail
            label="Avg duration"
            value={
              totals.avg_duration_seconds
                ? formatDuration(totals.avg_duration_seconds)
                : '—'
            }
          />
          <Detail
            label="Input tokens"
            value={totals.total_input_tokens.toLocaleString()}
          />
          <Detail
            label="Output tokens"
            value={totals.total_output_tokens.toLocaleString()}
          />
        </div>
      </ChartCard>
    </div>
  )
}


function categoryLabel(value: string): string {
  return value.split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
}


function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
}


function StatCard({
  label,
  value,
  accent,
  tooltip,
}: {
  label: string
  value: string
  accent?: 'neutral' | 'emerald' | 'amber'
  tooltip?: string
}) {
  return (
    <div
      title={tooltip}
      className={cn(
        'rounded-lg border bg-white p-3 shadow-sm dark:bg-[#131313]',
        accent === 'emerald' && 'border-emerald-200 dark:border-emerald-900',
        accent === 'amber' && 'border-amber-200 dark:border-amber-900',
        (!accent || accent === 'neutral') && 'border-neutral-200 dark:border-neutral-800',
      )}
    >
      <p className="text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-neutral-900 dark:text-neutral-100">{value}</p>
    </div>
  )
}


function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm dark:border-neutral-800 dark:bg-[#131313]">
      <h2 className="mb-3 text-sm font-semibold text-neutral-700 dark:text-neutral-200">{title}</h2>
      {children}
    </section>
  )
}


function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className="mt-0.5 text-base font-medium text-neutral-900 dark:text-neutral-100 tabular-nums">{value}</p>
    </div>
  )
}


function LoadingState() {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500" />
    </div>
  )
}


function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
      Couldn&apos;t load analytics: {message}
    </div>
  )
}


function EmptyChart({ message }: { message: string }) {
  return (
    <div className="flex h-40 items-center justify-center text-sm text-neutral-500 dark:text-neutral-400">
      {message}
    </div>
  )
}
